/**
 * Сезоны, недельные события и сезонный пропуск.
 *  - Сезон = календарный месяц (местное время). Прогресс пропуска сбрасывается с новым сезоном.
 *  - Событие: каждые пятница–воскресенье, по кругу «Праздник урожая» → «Неделя опыта» → «Награды ×2».
 *  - Очки пропуска считаются из статистики с начала сезона (harvests, fishCaught, questsDone…), поэтому
 *    никаких дополнительных «хуков» в игровой логике не нужно.
 */
import type { GameState, L10n, PassState } from './types';
import { applyGrants, type PackGrants, type PackResult } from './grants';

// ───────────────────────── сезоны

export interface SeasonInfo { id: string; index: number; name: L10n; endsAt: number; daysLeft: number }

const THEMES: L10n[] = [
  { zh: '冬日童话', en: 'Winter Tale', ru: 'Зимняя сказка' },
  { zh: '雪融时节', en: 'Thaw', ru: 'Первая оттепель' },
  { zh: '樱花盛开', en: 'Blossom Season', ru: 'Время цветения' },
  { zh: '春耕时节', en: 'Spring Planting', ru: 'Весенний сев' },
  { zh: '花海之月', en: 'Flower Month', ru: 'Месяц цветов' },
  { zh: '夏日海滩', en: 'Beach Summer', ru: 'Пляжное лето' },
  { zh: '热浪来袭', en: 'Heat Wave', ru: 'Жаркий июль' },
  { zh: '丰收前夜', en: 'Harvest Eve', ru: 'Канун урожая' },
  { zh: '金秋开学', en: 'Golden Autumn', ru: 'Золотая осень' },
  { zh: '南瓜之月', en: 'Pumpkin Month', ru: 'Тыквенный месяц' },
  { zh: '雨季来临', en: 'Rainy Season', ru: 'Время дождей' },
  { zh: '节日海岛', en: 'Festive Isle', ru: 'Праздничный остров' },
];

export function seasonAt(now: number): SeasonInfo {
  const d = new Date(now);
  const y = d.getFullYear(), m = d.getMonth();
  const endsAt = new Date(y, m + 1, 1).getTime();
  return {
    id: `${y}-${String(m + 1).padStart(2, '0')}`,
    index: y * 12 + m,
    name: THEMES[m],
    endsAt,
    daysLeft: Math.max(0, Math.ceil((endsAt - now) / 86_400_000)),
  };
}

// ───────────────────────── недельные события

export type EventId = 'harvest' | 'xp' | 'bounty';
export interface EventInfo {
  id: EventId;
  icon: string;
  name: L10n;
  desc: L10n;
  endsAt: number;
  sellMult: number;   // цена продажи
  xpMult: number;     // опыт
  dailyMult: number;  // ежедневный бонус и очки пропуска от него
}

const EVENTS: Omit<EventInfo, 'endsAt'>[] = [
  { id: 'harvest', icon: '🌾', sellMult: 1.5, xpMult: 1, dailyMult: 1,
    name: { zh: '丰收节', en: 'Harvest Festival', ru: 'Праздник урожая' },
    desc: { zh: '所有产物售价 ×1.5', en: 'Sell prices ×1.5', ru: 'Цены продажи ×1,5' } },
  { id: 'xp', icon: '⭐', sellMult: 1, xpMult: 2, dailyMult: 1,
    name: { zh: '双倍经验周', en: 'Double XP Weekend', ru: 'Двойной опыт' },
    desc: { zh: '经验 ×2', en: 'XP ×2', ru: 'Опыт ×2' } },
  { id: 'bounty', icon: '🎁', sellMult: 1, xpMult: 1, dailyMult: 2,
    name: { zh: '双倍奖励', en: 'Double Rewards', ru: 'Награды ×2' },
    desc: { zh: '每日奖励 ×2', en: 'Daily rewards ×2', ru: 'Ежедневные награды ×2' } },
];

/** Активное событие: пятница, суббота и воскресенье (по местному времени). */
export function activeEvent(now: number): EventInfo | null {
  const d = new Date(now);
  const dow = d.getDay();                       // 0 вс … 6 сб
  if (dow !== 5 && dow !== 6 && dow !== 0) return null;
  const dayNum = Math.floor(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86_400_000);
  // неделя считается с понедельника: воскресенье относится к той же неделе, что и предыдущая пятница
  const week = Math.floor((dayNum + 3) / 7);
  const daysToMon = (8 - dow) % 7 || 7;
  const endsAt = new Date(d.getFullYear(), d.getMonth(), d.getDate() + daysToMon).getTime();
  return { ...EVENTS[((week % EVENTS.length) + EVENTS.length) % EVENTS.length], endsAt };
}

let provider: () => EventInfo | null = () => null;
/** Игра подключает реальные часы; тесты и сервер остаются без события (множитель 1). */
export function setEventProvider(fn: () => EventInfo | null): void { provider = fn; }
export function eventMult(kind: 'sell' | 'xp' | 'daily'): number {
  const e = provider();
  if (!e) return 1;
  return kind === 'sell' ? e.sellMult : kind === 'xp' ? e.xpMult : e.dailyMult;
}

// ───────────────────────── сезонный пропуск

export const MAX_TIER = 30;
export const POINTS_PER_TIER = 100;
/** За что начисляются очки пропуска: вес × прирост статистики с начала сезона. */
export const PASS_WEIGHTS: Record<string, number> = {
  harvests: 1, fishCaught: 2, questsDone: 15, beastsDefeated: 3, animalsCollected: 1, helps: 2,
};

const statNum = (state: GameState, k: string): number => Number((state.stats as Record<string, number | string>)[k] ?? 0) || 0;

/** Гарантирует состояние пропуска текущего сезона (новый сезон — новый пропуск). */
export function ensurePass(state: GameState, now: number): PassState {
  const season = seasonAt(now).id;
  if (!state.pass || state.pass.season !== season) {
    const base: Record<string, number> = {};
    for (const k of Object.keys(PASS_WEIGHTS)) base[k] = statNum(state, k);
    state.pass = { season, premium: false, base, bonus: 0, claimedFree: [], claimedPrem: [] };
  }
  return state.pass;
}

export function passPoints(state: GameState): number {
  const ps = state.pass;
  if (!ps) return 0;
  let pts = ps.bonus;
  for (const [k, w] of Object.entries(PASS_WEIGHTS)) pts += w * Math.max(0, statNum(state, k) - (ps.base[k] ?? 0));
  return Math.floor(pts);
}
export const passTier = (state: GameState): number => Math.min(MAX_TIER, Math.floor(passPoints(state) / POINTS_PER_TIER));
export function addPassBonus(state: GameState, points: number, now: number): void { ensurePass(state, now).bonus += points; }

export type PassTrack = 'free' | 'prem';

// Призовые наборы премиум-дорожки: меняются от сезона к сезону. Только предметы, которые уже есть в игре.
const PREMIUM_SPECIALS: Record<number, PackGrants>[] = [
  { 5: { clothing: ['cl_hat_pirate'] }, 10: { clothing: ['cl_top_pirate'] }, 15: { pets: ['pet_fox'] }, 20: { clothing: ['cl_acc_parrot'] }, 25: { buildings: ['bld_statue'] }, 30: { pets: ['pet_dolphin'], coins: 30_000 } },
  { 5: { clothing: ['cl_hat_shark'] }, 10: { clothing: ['cl_top_kimono'] }, 15: { pets: ['pet_parrot'] }, 20: { clothing: ['cl_acc_wings'] }, 25: { buildings: ['bld_hotspring'] }, 30: { clothing: ['cl_top_royal', 'cl_hat_crown'], coins: 30_000 } },
  { 5: { clothing: ['cl_hair_wave_silver'] }, 10: { clothing: ['cl_top_suit'] }, 15: { pets: ['pet_turtle'] }, 20: { buildings: ['bld_windmill'] }, 25: { buildings: ['bld_lighthouse'] }, 30: { buildings: ['bld_castle'], coins: 30_000 } },
  { 5: { clothing: ['cl_hat_flower'] }, 10: { clothing: ['cl_top_hoodie'] }, 15: { animals: ['animal_alpaca'] }, 20: { animals: ['animal_cow'] }, 25: { animals: ['animal_peacock'] }, 30: { buildings: ['bld_ferris'], pets: ['pet_piglet'] } },
];
const FREE_SPECIALS: Record<number, PackGrants> = {
  10: { clothing: ['cl_acc_shell'], coins: 3_000 },
  20: { buildings: ['bld_fountain'], coins: 6_000 },
  30: { clothing: ['cl_top_hoodie'], coins: 20_000 },
};

export function passReward(seasonIndex: number, tier: number, track: PassTrack): PackGrants {
  if (track === 'free') {
    const sp = FREE_SPECIALS[tier];
    if (sp) return sp;
    return tier % 2 === 1 ? { coins: 1_000 + 250 * tier } : { food: 100 + 20 * tier, ...(tier % 5 === 0 ? { fullEnergy: true } : {}) };
  }
  const set = PREMIUM_SPECIALS[((seasonIndex % 4) + 4) % 4];
  const base: PackGrants = { coins: 2_500 + 600 * tier };
  const sp = set[tier];
  if (sp) return { ...base, ...sp, coins: (sp.coins ?? 0) + (base.coins ?? 0) };
  if (tier % 3 === 0) base.xp = 60 * tier;
  if (tier % 4 === 0) base.fullEnergy = true;
  return base;
}

export type ClaimFail = 'locked' | 'premium' | 'claimed';
export function canClaimPass(state: GameState, tier: number, track: PassTrack): true | ClaimFail {
  const ps = state.pass;
  if (!ps || tier < 1 || tier > passTier(state)) return 'locked';
  if (track === 'prem' && !ps.premium) return 'premium';
  return (track === 'free' ? ps.claimedFree : ps.claimedPrem).includes(tier) ? 'claimed' : true;
}

export function claimPassReward(state: GameState, tier: number, track: PassTrack, now: number, petName: (id: string) => string): { ok: true; res: PackResult } | { ok: false; reason: ClaimFail } {
  ensurePass(state, now);
  const can = canClaimPass(state, tier, track);
  if (can !== true) return { ok: false, reason: can };
  (track === 'free' ? state.pass!.claimedFree : state.pass!.claimedPrem).push(tier);
  return { ok: true, res: applyGrants(state, passReward(seasonAt(now).index, tier, track), now, petName) };
}

/** Сколько наград можно забрать прямо сейчас (для красной точки на кнопке). */
export function claimablePassCount(state: GameState): number {
  let n = 0;
  for (let t = 1; t <= passTier(state); t++) {
    if (canClaimPass(state, t, 'free') === true) n++;
    if (canClaimPass(state, t, 'prem') === true) n++;
  }
  return n;
}
